/**
 * Authentication Store for Cedar Heights Music Academy Admin System
 * 
 * This Zustand store manages authentication state including:
 * - User login/logout functionality
 * - JWT token management
 * - Role-based access control
 * - Session persistence
 * - Authentication status tracking
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, UserRole, AuthState } from '../types';
import { ApiError } from '../services/api';
import { API_BASE_URL } from '../services/config';

// Global error notification function type
type NotificationFunction = (message: string, options?: {
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  persistent?: boolean;
  autoHideDuration?: number;
}) => string;

// Global notification function - will be set by the app initialization
let globalNotificationFunction: NotificationFunction | null = null;

/**
 * Set the global notification function for auth store error reporting
 */
export const setAuthNotificationFunction = (notificationFn: NotificationFunction) => {
  globalNotificationFunction = notificationFn;
};

/**
 * Show global notification for auth errors
 */
const showAuthError = (message: string, title?: string) => {
  if (globalNotificationFunction) {
    globalNotificationFunction(message, {
      severity: 'error',
      title: title || 'Authentication Error',
      persistent: true,
      autoHideDuration: 10000, // 10 seconds for auth errors
    });
  }
};

/**
 * Show global notification for auth info
 */
const showAuthInfo = (message: string, title?: string) => {
  if (globalNotificationFunction) {
    globalNotificationFunction(message, {
      severity: 'info',
      title: title || 'Authentication',
      autoHideDuration: 5000, // 5 seconds for info messages
    });
  }
};

// Extended auth state interface for the store
interface AuthStore extends AuthState {
  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  clearError: () => void;
  refreshToken: () => Promise<void>;
  switchRole: (role: 'admin' | 'teacher') => void;
  updateProfile: (profileData: Partial<User>) => Promise<void>;
  verifyToken: () => Promise<boolean>;
  getCurrentUser: () => Promise<void>;
  
  // Getters
  isAdmin: () => boolean;
  isTeacher: () => boolean;
  isParent: () => boolean;
  canSwitchRoles: () => boolean;
  getCurrentRole: () => 'admin' | 'teacher' | 'parent';
}

/**
 * Authentication service interface - matches backend API format
 */
interface AuthResponse {
  success: boolean;
  token: string;
  user: {
    user_id: string;
    email: string;
    role: string;
    permissions: string[];
    user_data: Record<string, unknown>;
    authenticated: boolean;
    auth_type: string;
  };
  error: string | null;
}

interface RefreshResponse {
  success: boolean;
  token: string;
}

/**
 * Real authentication service using backend API
 */
const authService = {
  /**
   * Login user with email and password
   */
  login: async (email: string, password: string): Promise<{ user: User; token: string }> => {
    try {
      // Use centralized API base URL
      const apiBaseUrl = API_BASE_URL;
      
      // Use fetch directly for auth endpoints to avoid API wrapper complications
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid email or password');
        }
        if (response.status === 403) {
          throw new Error('Account is disabled');
        }
        if (response.status === 422) {
          throw new Error('Invalid login credentials format');
        }
        const errorText = await response.text().catch(() => 'Login failed');
        throw new Error(errorText);
      }

      const data = await response.json();
      
      // Handle both direct AuthResponse format and wrapped ApiResponse format
      const authData = data.data ? data.data : data;

      if (!authData.success) {
        throw new Error(authData.error || 'Login failed');
      }

      // Convert role to lowercase for frontend User type
      const convertedRole = authData.user.role.toLowerCase() as UserRole;

      // Map backend user format to frontend User type
      const user: User = {
        id: authData.user.user_id,
        email: authData.user.email,
        role: convertedRole,
        firstName: 'User', // Will be updated from user metadata if available
        lastName: 'Name',  // Will be updated from user metadata if available
        isActive: authData.user.authenticated,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      return {
        user,
        token: authData.token,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        // Handle specific API errors
        if (error.status === 401) {
          throw new Error('Invalid email or password');
        }
        if (error.status === 403) {
          throw new Error('Account is disabled');
        }
        if (error.status === 422) {
          throw new Error('Invalid login credentials format');
        }
        throw new Error(error.message || 'Login failed');
      }
      if (error instanceof TypeError) {
        throw new Error('Unable to reach the Cedar Heights API. Please check the connection and try again.');
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Login failed. Please try again.');
    }
  },

  /**
   * Refresh JWT token
   */
  refreshToken: async (token: string): Promise<{ token: string }> => {
    try {
      // Use centralized API base URL
      const apiBaseUrl = API_BASE_URL;
      
      // Use fetch directly for auth endpoints to avoid API wrapper complications
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Token expired or invalid');
        }
        const errorText = await response.text().catch(() => 'Token refresh failed');
        throw new Error(errorText);
      }

      const data = await response.json();
      
      // Handle both direct RefreshResponse format and wrapped ApiResponse format
      const refreshData = data.data ? data.data : data;

      if (!refreshData.success) {
        throw new Error('Token refresh failed');
      }

      return {
        token: refreshData.token,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Token expired or invalid');
        }
        throw new Error(error.message || 'Token refresh failed');
      }
      throw new Error('Network error during token refresh');
    }
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      // Use centralized API base URL
      const apiBaseUrl = API_BASE_URL;
      
      // Use fetch directly for auth endpoints to avoid API wrapper complications
      await fetch(`${apiBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      // Log error but don't throw - logout should always succeed locally
      console.warn('Logout API call failed:', error);
    }
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use centralized API base URL
      const apiBaseUrl = API_BASE_URL;
      
      // Use fetch directly for auth endpoints to avoid API wrapper complications
      const response = await fetch(`${apiBaseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          throw new ApiError('Authentication required', 401, 'UNAUTHORIZED');
        }
        if (response.status === 403) {
          throw new ApiError('Access forbidden', 403, 'FORBIDDEN');
        }
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new ApiError(`HTTP ${response.status}: ${errorText}`, response.status, 'API_ERROR');
      }

      // The /auth/me endpoint returns the user data directly (not wrapped)
      const userData = await response.json();
      
      // Map backend user format to frontend User type
      const user: User = {
        id: userData.user_id,
        email: userData.email,
        role: userData.role.toLowerCase() as UserRole,
        firstName: userData.user_data?.first_name || 'User',
        lastName: userData.user_data?.last_name || 'Name',
        isActive: userData.authenticated,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      return user;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        throw new Error(error.message || 'Failed to get user profile');
      }
      throw new Error('Network error while fetching user profile');
    }
  },

  /**
   * Update user profile
   */
  updateProfile: async (profileData: Partial<User>): Promise<User> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use centralized API base URL
      const apiBaseUrl = API_BASE_URL;
      
      // Use fetch directly for auth endpoints to avoid API wrapper complications
      const response = await fetch(`${apiBaseUrl}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          throw new ApiError('Authentication required', 401, 'UNAUTHORIZED');
        }
        if (response.status === 422) {
          throw new ApiError('Invalid profile data', 422, 'VALIDATION_ERROR');
        }
        const errorText = await response.text().catch(() => 'Failed to update profile');
        throw new Error(errorText);
      }

      const data = await response.json();
      
      // Handle both direct response format and wrapped ApiResponse format
      const profileResponse = data.data ? data.data : data;
      
      if (!profileResponse.success) {
        throw new Error('Failed to update profile');
      }

      return profileResponse.user;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        if (error.status === 422) {
          throw new Error('Invalid profile data');
        }
        throw new Error(error.message || 'Failed to update profile');
      }
      throw new Error('Network error while updating profile');
    }
  },

  /**
   * Verify token validity
   */
  verifyToken: async (token: string): Promise<boolean> => {
    try {
      // Use centralized API base URL
      const apiBaseUrl = API_BASE_URL;
      
      // Use fetch directly for auth endpoints to avoid API wrapper complications
      const response = await fetch(`${apiBaseUrl}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      
      // Handle both direct response format and wrapped ApiResponse format
      const verifyData = data.data ? data.data : data;
      
      return verifyData.success && verifyData.valid;
    } catch {
      // If verification fails, assume token is invalid
      return false;
    }
  },
};

/**
 * Authentication store using Zustand with persistence
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const { user, token } = await authService.login(email, password);
          
          // Store token in localStorage (in production, consider more secure storage)
          localStorage.setItem('auth_token', token);
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          
          // Show global notification for login errors
          showAuthError(errorMessage, 'Login Failed');
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        
        try {
          await authService.logout();
          
          // Clear token from localStorage
          localStorage.removeItem('auth_token');
          
          // Show logout success notification
          showAuthInfo('You have been logged out successfully.', 'Logged Out');
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        } catch {
          // Even if logout fails, clear local state and show notification
          localStorage.removeItem('auth_token');
          showAuthInfo('You have been logged out successfully.', 'Logged Out');
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      setUser: (user: User) => {
        set({
          user,
          isAuthenticated: true,
          error: null,
        });
      },

      clearError: () => {
        set({ error: null });
      },

      refreshToken: async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          throw new Error('No token found');
        }

        try {
          const { token: newToken } = await authService.refreshToken(token);
          localStorage.setItem('auth_token', newToken);
        } catch (refreshError) {
          // Show error notification for token refresh failure
          showAuthError('Your session has expired. Please log in again.', 'Session Expired');
          
          // If refresh fails, logout user
          get().logout();
          throw refreshError;
        }
      },

      switchRole: (role: 'admin' | 'teacher') => {
        const { user } = get();
        if (!user || !get().canSwitchRoles()) {
          return;
        }

        // Update user role (this would typically be an API call)
        set({
          user: {
            ...user,
            role: role as UserRole,
          },
        });
      },

      updateProfile: async (profileData: Partial<User>) => {
        set({ isLoading: true, error: null });
        
        try {
          const updatedUser = await authService.updateProfile(profileData);
          
          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });
        } catch (updateError) {
          const errorMessage = updateError instanceof Error ? updateError.message : 'Profile update failed';
          
          // Show error notification for profile update failure
          showAuthError(errorMessage, 'Profile Update Failed');
          
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw updateError;
        }
      },

      verifyToken: async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          return false;
        }

        try {
          const isValid = await authService.verifyToken(token);
          if (!isValid) {
            // Token is invalid, clear auth state
            get().logout();
          }
          return isValid;
        } catch {
          // If verification fails, assume token is invalid
          get().logout();
          return false;
        }
      },

      getCurrentUser: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const user = await authService.getCurrentUser();
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to get user profile';
          
          // Show error notification for getCurrentUser failure
          showAuthError(errorMessage, 'Profile Load Failed');
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      // Getters
      isAdmin: () => {
        const { user } = get();
        return user?.role === 'admin';
      },

      isTeacher: () => {
        const { user } = get();
        return user?.role === 'teacher';
      },

      isParent: () => {
        const { user } = get();
        return user?.role === 'parent';
      },

      canSwitchRoles: () => {
        const { user } = get();
        // Only admin users can switch to teacher role
        return user?.role === 'admin';
      },

      getCurrentRole: () => {
        const { user } = get();
        const currentRole = user?.role || 'parent';
        
        // // INVESTIGATION LOG: Track role retrieval and fallback
        // console.log('[AUTH_INVESTIGATION] getCurrentRole called:', {
        //   timestamp: new Date().toISOString(),
        //   userExists: !!user,
        //   userRole: user?.role,
        //   userRoleType: typeof user?.role,
        //   fallbackTriggered: !user?.role,
        //   returnedRole: currentRole,
        //   returnedRoleType: typeof currentRole
        // });
        
        return currentRole;
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist user and authentication status, not loading/error states
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selectors for common use cases
export const authSelectors = {
  user: (state: AuthStore) => state.user,
  isAuthenticated: (state: AuthStore) => state.isAuthenticated,
  isLoading: (state: AuthStore) => state.isLoading,
  error: (state: AuthStore) => state.error,
  isAdmin: (state: AuthStore) => state.isAdmin(),
  isTeacher: (state: AuthStore) => state.isTeacher(),
  isParent: (state: AuthStore) => state.isParent(),
  canSwitchRoles: (state: AuthStore) => state.canSwitchRoles(),
  currentRole: (state: AuthStore) => state.getCurrentRole(),
};

// Custom hooks for common authentication patterns
export const useAuth = () => {
  const store = useAuthStore();
  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    login: store.login,
    logout: store.logout,
    clearError: store.clearError,
    refreshToken: store.refreshToken,
    updateProfile: store.updateProfile,
    verifyToken: store.verifyToken,
    getCurrentUser: store.getCurrentUser,
  };
};

export const useAuthRole = () => {
  const store = useAuthStore();
  const currentRole = store.getCurrentRole();
  const isAdmin = store.isAdmin();
  const isTeacher = store.isTeacher();
  const isParent = store.isParent();
  
  // // INVESTIGATION LOG: Track role hook usage
  // console.log('[AUTH_INVESTIGATION] useAuthRole hook called:', {
  //   timestamp: new Date().toISOString(),
  //   currentRole,
  //   currentRoleType: typeof currentRole,
  //   isAdmin,
  //   isTeacher,
  //   isParent,
  //   canSwitchRoles: store.canSwitchRoles(),
  //   storeUser: store.user,
  //   storeUserRole: store.user?.role
  // });
  
  return {
    isAdmin,
    isTeacher,
    isParent,
    canSwitchRoles: store.canSwitchRoles(),
    currentRole,
    switchRole: store.switchRole,
  };
};

export default useAuthStore;
